import React, { useState, useRef, useCallback, memo } from 'react';
import { X } from 'lucide-react';
import { submitMessageFeedback } from '../../../api/chats';

import styles from './styles/FeedbackForm.module.css';

const FEEDBACK_OPTIONS = [
    { value: 'STYLE_ISSUE', label: "Don't like the style" },
    { value: 'FACTUAL_ERROR', label: 'Not factually correct' },
    { value: 'INCOMPLETE', label: "Didn't fully follow instructions" },
    { value: 'REFUSED', label: "Refused when it shouldn't have" },
    { value: 'TOOL_ERROR', label: 'Incorrect operation of tools' },
    { value: 'SLOW_RESPONSE', label: 'Too slow response' },
    { value: 'OTHER', label: 'More...' },
];

const FeedbackForm = ({ messageId, onClose }) => {
    const [selectedOption, setSelectedOption] = useState('');
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCustomInput, setShowCustomInput] = useState(false);
    const formRef = useRef(null);

    const handleSubmit = useCallback(async e => {
        e.preventDefault();

        if (!selectedOption && !comment) {
            return;
        }

        try {
            setIsSubmitting(true);

            await submitMessageFeedback(messageId, {
                feedbackType: 'BAD',
                commentType: selectedOption,
                comment: selectedOption === 'OTHER' ? comment : null,
            });
            onClose();
        } catch (error) {
            console.error('Error saving feedback:', error);
        } finally {
            setIsSubmitting(false);
        }
    }, [messageId, selectedOption, comment, onClose]);

    const handleOptionClick = useCallback(async option => {
        try {
            if (option !== 'OTHER') {
                setIsSubmitting(true);

                await submitMessageFeedback(messageId, {
                    feedbackType: 'BAD',
                    commentType: option,
                    comment: FEEDBACK_OPTIONS.find(o => o.value === option).label || null,
                });
                onClose();
            } else {
                setSelectedOption(option);
                setShowCustomInput(true);
            }
        } catch (error) {
            console.error('Error saving feedback:', error);
        } finally {
            setIsSubmitting(false);
        }
    }, [messageId, onClose]);

    return (
        <div className={styles.formContainer}>
            <div className={styles.formWrapper} ref={formRef}>
                <div className={styles.formHeader}>
                    <p>Tell us more:</p>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={styles.optionsContainer}>
                        {FEEDBACK_OPTIONS.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className={`${styles.optionButton} ${selectedOption === option.value ? styles.selected : ''}`}
                                onClick={() => handleOptionClick(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {showCustomInput && (
                        <>
                            <div className={styles.customInputContainer}>
                                <textarea
                                    value={comment}
                                    onChange={e => setComment(e.target.value)}
                                    placeholder="Please tell us what went wrong..."
                                    className={styles.customInput}
                                    rows={3}
                                />
                            </div>

                            <div className={styles.buttonContainer}>
                                <button
                                    type="submit"
                                    className={styles.submitButton}
                                    disabled={isSubmitting || (!selectedOption && !comment)}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </>
                    )}
                </form>
            </div>
        </div>
    );
};

export default memo(FeedbackForm);
